<template>
    <li class="flex flex-col">
        <button
            ref="accordionButton"
            @click="setSelectedGroup(itemData)"
            :disabled="editDisabled"
            :class="{ 'cursor-not-allowed opacity-50' : editDisabled }"
            class="border-b border-gray bg-white box-content px-10 h-[90px] flex items-center flexbox-big-gap-hack-wrapper"
        >
            <div
                class="relative h-full w-32 flex-none bg-center bg-no-repeat bg-contain"
                :style="{
                    backgroundImage: groupImage,
                }"
            >
                <transition name="cz3__fade">
                    <span
                        v-if="showCheckmark"
                        class="absolute top-1 left-1 text-green font-black text-xl"
                    >
                        &check;
                    </span>
                </transition>
            </div>

            <div
                :class="{ 'text-red': hasErrors }"
                class="flex-grow text-left flexbox-big-gap-hack-item"
            >
                <p class="text-base font-bold font-aeonik">
                    {{ groupName }}
                </p>

                <base-error-message :category-name="itemData.name" />
            </div>

            <current-selection
                :placement-data="itemData"
                class="w-24 text-right flexbox-big-gap-hack-item"
            />

            <img
                v-if="!editDisabled"
                src="../assets/images/icons/arrow-dropdown.svg"
                width="10"
                height="5"
                alt="Dropdown arrow"
                :class="{ '-rotate-180': isActive }"
                class="py-3 transition-transform duration-300 ease-in-out flexbox-big-gap-hack-item"
            >
        </button>

        <div
            :style="{ 'height': `${elementHeight}px` }"
            :class="{ 'border-b border-gray': isActive }"
            class="overflow-hidden transition-all duration-300 ease-in-out flexbox-big-gap-hack-item"
        >
            <div
                ref="accordionItem"
                class="py-6 px-10"
            >
                <step-options
                    v-if="isActive"
                    :item-data="itemData"
                />
            </div>
        </div>
    </li>
</template>

<script>
    import allStores from '../stores/all';
    import * as constants from '../constants';

    import CurrentSelection from './CurrentSelection.vue';
    import StepOptions from './StepOptions.vue';
    import BaseErrorMessage from './BaseErrorMessage.vue';

    import { autoWebp } from '../helpers/images';

    export default {
        mixins: [allStores],

        components: {
            CurrentSelection,
            StepOptions,
            BaseErrorMessage,
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                isActive: false,
                interval: null,
                scrollHeight: 0,
                elementHeight: 0,
            };
        },

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            groupName() {
                const editedName = this.itemData.name.replace(' Color', '');
                let translatedName = this.customizerStore.useTranslations(this.itemData.custom?.['fj-translation-code'], editedName);

                if (this.disableEdit) {
                    translatedName = translatedName.replace('Select ', '');
                }

                return translatedName;
            },

            inErrorState() {
                return this.customizerStore.inErrorState;
            },

            hasBackorderedError() {
                return this.customizerStore.hasBackorderedError;
            },

            hasBackorderedSize() {
                return this.customizerStore.hasBackorderedSize;
            },

            selectionWasMade() {
                return this.customizerStore.selectionsMade.includes(this.itemData.name);
            },

            cartErrors() {
                return this.customizerStore.cartErrors;
            },

            hasSizeErrors() {
                return (this.hasBackorderedError && this.itemData.name.includes('Size'));
            },

            hasErrors() {
                return (this.inErrorState && !this.selectionWasMade) || this.hasSizeErrors;
            },

            showCheckmark() {
                return this.selectionWasMade && !this.hasSizeErrors && !this.hasBackorderedSize;
            },

            isReset() {
                return this.appStore.currentStep === constants.STEP_RESET;
            },

            /**
             * Grab the first accordion image from any placement in this group.
             */
            groupImage() {
                let placementWithImage = null;

                const firstPlacement = this.itemData?.placements?.[0];
                if (firstPlacement) {
                    placementWithImage = this.viewController.blueprint.custom[`fj-accordion-image-${firstPlacement.code}`];

                    if (placementWithImage) {
                        return `url(${autoWebp(placementWithImage)})`;
                    }
                }

                placementWithImage = this.itemData?.placements?.find((placement) => placement.custom?.['fj-accordion-image']);

                if (placementWithImage) {
                    return `url(${autoWebp(placementWithImage.custom['fj-accordion-image'])})`;
                }

                // Is there a group image?
                if (this.itemData.custom?.['fj-accordion-image']) {
                    return `url(${autoWebp(this.itemData.custom['fj-accordion-image'])})`;
                }

                return '';
            },

            currentGroup() {
                return this.customizerStore.currentGroup;
            },

            isCurrentGroup() {
                return this.itemData.name === this.currentGroup;
            },

            disableEdit() {
                return this.customizerStore.disableEdit;
            },

            editDisabled() {
                return this.itemData.name !== 'Size & Width' && this.disableEdit;
            },
        },

        watch: {
            currentGroup() {
                // Close accordion if another group is selected
                if (!this.isCurrentGroup) {
                    this.isActive = false;
                    this.scrollHeight = 0;
                    clearInterval(this.interval);
                    this.$refs.accordionButton.classList.remove('sticky', 'top-0', 'z-10');
                }
            },

            scrollHeight() {
                this.elementHeight = this.scrollHeight;
            },

            cartErrors() {
                // Collapse accordions when error state is triggered
                if (this.cartErrors) {
                    this.resetAccordions();
                }
            },

            isReset() {
                // Collapse accordions when app is reset
                if (this.isReset) {
                    this.resetAccordions();
                }
            },
        },

        mounted() {
            if (this.shouldActivateOnLoad()) {
                if (!this.isActive) {
                    this.toggleAccordion();
                }
            }
        },

        unmounted() {
            clearInterval(this.interval);
        },

        methods: {
            shouldActivateOnLoad() {
                if (this.itemData.code === 'Size & Width') {
                    if (this.customizerStore.preferDifferentSizes()) {
                        return true;
                    }
                }

                return false;
            },

            toggleAccordion() {
                this.isActive = !this.isActive;

                if (this.isActive) {
                    this.interval = setInterval(() => {
                        if (this.$refs.accordionItem) {
                            this.scrollHeight = this.$refs.accordionItem.scrollHeight;
                        }
                    }, 100);

                    // Leave time for scroll height to be set and animation to complete
                    setTimeout(() => {
                        // Include boolean parameter for Safari 14
                        this.$refs.accordionButton.scrollIntoView(true);

                        setTimeout(() => {
                            this.$refs.accordionButton.classList.add('sticky', 'top-0', 'z-10');
                        }, 200);
                    }, 500);
                } else {
                    this.scrollHeight = 0;
                    clearInterval(this.interval);

                    this.$refs.accordionButton.classList.remove('sticky', 'top-0', 'z-10');
                }
            },

            setSelectedGroup(itemData) {
                if (!this.editDisabled) {
                    // Reset current group if it's already the selected group
                    if (this.isActive) {
                        this.customizerStore.setCurrentGroup(null);
                    }

                    this.toggleAccordion();

                    if (this.isActive) {
                        // Set the selected group
                        this.customizerStore.setCurrentGroup(itemData.name);

                        // Set currentView to the selected group's view
                        this.customizerStore.setCurrentViewByGroup(itemData);
                    }
                }
            },

            resetAccordions() {
                this.isActive = false;
                this.scrollHeight = 0;
                clearInterval(this.interval);

                this.$emit('scrollToTop');
            },
        },
    };
</script>
